/**
 * As duas telas da ficha da Empresa (história 2.1).
 *
 *     /empresas/nova          cadastrar
 *     /empresas/$empresaId    editar
 *
 * As duas são filhas de `rotaDaAplicacao`, e por isso herdam a guarda de sessão do layout —
 * registrada uma vez lá, ela vale para toda rota interna que nascer daqui em diante.
 *
 * **`/empresas` é a listagem (2.6), em `src/rotas/empresas.tsx`.** As duas rotas abaixo são
 * caminhos **irmãos** dela na árvore (todos filhos do mesmo layout), e não filhos dela:
 * `/empresas` não é um layout com `<Outlet/>`. `nova` vem antes do parâmetro na declaração, e o
 * TanStack Router prefere a rota estática — `/empresas/nova` nunca é lido como um `empresaId`
 * chamado "nova".
 *
 * Depois de salvar, as duas vão para a ficha em modo de edição: quem acabou de cadastrar
 * costuma completar o que faltou. Desde a 2.6 há também o caminho de volta — a listagem liga a
 * razão social a esta tela, e traz o botão "Nova empresa" para a de cadastro.
 *
 * **E desde a 5.1 há um terceiro caminho para a mesma ficha**, e ele acontece antes de qualquer
 * gravação: o CNPJ digitado perde o foco, o `cadastro` confere no Carga Máquina, e a tela **abre a
 * ficha** daquela empresa — a que já existia, ou a que acabou de nascer já vinculada ao Carga
 * Máquina. Quem
 * navega é esta rota, e não o formulário: ele é montado em duas telas, e um `useNavigate` lá dentro
 * o amarraria a uma.
 *
 * **As seções irmãs aparecem só na tela de edição**, nesta ordem: o Responsável (2.5), os
 * Responsáveis pela empresa (2.2), o Grupo (13.9) e a sincronização com o ERP (5.5). Todas
 * pertencem a uma Empresa que já existe — cada uma é um ato com guarda e trilha próprias
 * sobre a ficha gravada —, e como o cadastro já navega para cá, ninguém precisa de várias telas
 * para completar a ficha.
 *
 * A ordem é a de quem decide sobre quem: o **Responsável** vem logo depois da ficha porque ele é
 * o primeiro ramo da matriz de visibilidade ("é responsável, vê"; o recorte por ficha da 2.4 saiu na
 * 13.11b), e os **contatos** depois porque quem não alcança a Empresa não alcança nenhum deles. A
 * **sincronização** fecha a tela porque ela é sobre o que sai daqui para fora — a ficha inteira,
 * com os contatos dentro —, e porque a ação dela (reenviar) só faz sentido depois de a pessoa ter
 * conferido o que vai subir. Desde a 13.11c ela aparece sempre, dizendo se a Empresa está ou não no
 * Carga Máquina.
 *
 * "Responsável" e "Pessoas" são dois conceitos, e por isso são duas seções com títulos
 * diferentes: o primeiro é gente da Orvalho, o segundo é a Pessoa de contato dentro da Empresa.
 *
 * **Desde a 11.7 a tela é uma ficha de coluna única centralizada** (`.ficha-da-empresa`), com
 * cartões abertos; **desde 28/09/2026 ela segue o protótipo v2**: Editar empresa (com o
 * **Responsável** dentro da grade, pela prop `responsavel` do formulário), Informações para
 * contato, Dados de endereço (+ entregas), **Pessoas inline** (a seção da 2.2, pela prop
 * `pessoas`), Redes sociais e o rodapé com a Faixa, Cancelar e Salvar. A sincronização continua
 * logo abaixo, na mesma coluna.
 */

import { Link, createRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { FormularioDeEmpresa } from '@/modulos/cadastro/FormularioDeEmpresa'
import { CONTATO_DO_WHATSAPP_VINCULADO, useEmpresa } from '@/modulos/cadastro/api'
import { nomeExibidoDaEmpresa } from '@/modulos/cadastro/nome-da-empresa'
import { useDefinirSubtitulo, useDefinirTitulo } from '@/shell/Shell'
import { CAMINHOS } from '@/shell/navegacao'
import { ResponsaveisDaEmpresa } from '@/modulos/cadastro/ResponsaveisDaEmpresa'
import { SincronizacaoComOErp } from '@/modulos/cadastro/SincronizacaoComOErp'
import { ExclusaoDaEmpresa } from '@/modulos/cadastro/ExclusaoDaEmpresa'
import { AvisoDoOrcamentoSemCnpj } from '@/modulos/cadastro/AvisoDoOrcamentoSemCnpj'
import { GrupoDaEmpresa } from '@/modulos/cadastro/GrupoDaEmpresa'
import { TrocaDeResponsavel } from '@/modulos/cadastro/TrocaDeResponsavel'
import { rotaDaAplicacao } from './aplicacao'

/*
 * O *state* do histórico que o "+ Empresa" leva à ficha aberta (13.4): uma linha de Pessoa da ficha
 * nova adotou a Pessoa solta do WhatsApp com o mesmo telefone.
 */
declare module '@tanstack/history' {
  interface HistoryState {
    contatoAdotado?: boolean
  }
}

/** O caminho da tela de cadastro. Escrito uma vez; quem precisa dele o importa. */
export const CAMINHO_DA_EMPRESA_NOVA = '/empresas/nova'

/** O caminho da tela de edição, com o `id` da ficha. */
export const CAMINHO_DA_EMPRESA = '/empresas/$empresaId'

/** O subtítulo do cabeçalho nas duas fichas (protótipo v2): de onde se veio. */
const SUBTITULO_DA_FICHA = 'Empresas'
const VOLTAR_A_EMPRESAS = '← Voltar a Empresas'

function VoltarAEmpresas() {
  return (
    <p className="ficha-da-empresa__voltar">
      <Link to={CAMINHOS.empresas}>{VOLTAR_A_EMPRESAS}</Link>
    </p>
  )
}

/*
 * A ficha nova (13.2): o formulário cria a Empresa e as Pessoas digitadas no cartão Pessoas, e só
 * depois da última chama `aoSalvar` — que abre a ficha. Com uma Pessoa recusada ele fica na tela
 * (a Empresa travada, "Salvar as pessoas"); "Seguir sem estas pessoas" também chega a `aoSalvar`.
 */
function EmpresaNova() {
  const navegar = useNavigate()
  useDefinirTitulo('Nova empresa')
  useDefinirSubtitulo(SUBTITULO_DA_FICHA)
  const abrirAFicha = (empresaId: string, contatoAdotado = false) => {
    void navegar({
      to: CAMINHO_DA_EMPRESA,
      params: { empresaId },
      // O aviso da adoção (13.4) vai pelo *state* do histórico, e não pela URL: ele é o desfecho
      // de um gesto, e não um endereço — a ficha o mostra uma vez e o limpa.
      ...(contatoAdotado ? { state: { contatoAdotado: true } } : {}),
    })
  }
  return (
    <div className="ficha-da-empresa">
      <VoltarAEmpresas />
      <FormularioDeEmpresa
        aoSalvar={(empresa, desfecho) => {
          abrirAFicha(empresa.id, desfecho?.contatoAdotado === true)
        }}
        /*
         * A consulta ao ERP (5.1) encontrou — ou acabou de criar — a ficha daquele CNPJ, e a tela
         * **abre a ficha existente** em vez de mostrar um aviso: é essa a diferença entre "não basta
         * avisar" e "não se cria segunda ficha" que o épico pede.
         *
         * Os dois desfechos com ficha chegam por aqui, e o destino é o **mesmo** de `aoSalvar`: uma
         * ficha aberta em modo de edição. O formulário não navega sozinho porque ele é montado em
         * duas rotas — a navegação é de quem tem o roteador.
         */
        aoEncontrarFicha={(empresaId) => {
          abrirAFicha(empresaId)
        }}
      />
    </div>
  )
}

function EmpresaEmEdicao() {
  const { empresaId } = rotaEmpresaEdicao.useParams()
  const navegar = useNavigate()
  /*
   * O aviso da adoção que o "+ Empresa" trouxe (13.4): lido **uma vez**, na chegada, e o *state* é
   * limpo em seguida com `replace` — o `history.state` sobrevive ao F5 e ao voltar/avançar, e sem a
   * limpeza o aviso reapareceria dias depois.
   */
  const contatoAdotado = useRouterState({ select: (estado) => estado.location.state.contatoAdotado })
  /* Preso ao `empresaId` de chegada: a rota não remonta quando só o parâmetro muda, e a ficha B
     aberta depois da A não pode herdar o aviso que era da A. */
  const [adocao] = useState(() => (contatoAdotado === true ? empresaId : null))
  const avisoDaAdocao = adocao === empresaId ? CONTATO_DO_WHATSAPP_VINCULADO : undefined
  useEffect(() => {
    if (contatoAdotado === true) {
      void navegar({ to: CAMINHO_DA_EMPRESA, params: { empresaId }, replace: true, state: {} })
    }
  }, [contatoAdotado, empresaId, navegar])
  /* O cabeçalho do shell com o nome da Empresa (protótipo v2): sem isto a ficha chegava sob o
     fallback "CRM Orvalho do Sol". A leitura é a mesma que o formulário faz — o cache é um só. */
  const empresa = useEmpresa(empresaId)
  //: 13.7: o título é o nome exibido (o nome fantasia); a razão social fica no formulário.
  useDefinirTitulo(empresa.data === undefined ? 'Empresa' : nomeExibidoDaEmpresa(empresa.data))
  useDefinirSubtitulo(SUBTITULO_DA_FICHA)
  // `key` remonta o formulário e as três seções quando o `id` muda: o rascunho do que a pessoa
  // digitou — e o aviso da última gravação — vive no estado dos componentes, e sem a remontagem
  // ele atravessaria de uma ficha para a outra.
  return (
    <div className="ficha-da-empresa">
      <VoltarAEmpresas />
      {/* 13.5 (FR-27): a ficha que recebeu orçamento sem CNPJ diz o que falta — antes do
          formulário, porque é nele que se preenche o CNPJ. */}
      <AvisoDoOrcamentoSemCnpj key={`aviso-${empresaId}`} empresaId={empresaId} />
      <FormularioDeEmpresa
        key={empresaId}
        empresaId={empresaId}
        /* Pessoas inline (11.7): a seção da 2.2 mora **dentro** da ficha, entre o Endereço e as
           Redes sociais. Aqui a Empresa já existe e cada linha grava na hora; na ficha nova
           (`/empresas/nova`, 13.2) o próprio formulário mostra as linhas e grava as Pessoas
           depois de criar a Empresa — por isso a prop só é passada aqui. */
        pessoas={
          <ResponsaveisDaEmpresa
            key={`responsaveis-${empresaId}`}
            empresaId={empresaId}
            {...(avisoDaAdocao === undefined ? {} : { avisoInicial: avisoDaAdocao })}
          />
        }
        /* O Responsável (2.5) na grade do primeiro cartão (protótipo v2): no modo de edição este
           é o **único** lugar onde ele é mostrado e trocado — o campo somente-leitura da criação
           sai justamente para que não haja duas cópias do mesmo valor divergindo na primeira
           gravação. */
        responsavel={<TrocaDeResponsavel key={`responsavel-${empresaId}`} empresaId={empresaId} />}
      />
      {/* 13.9 (FR-88): o Grupo de Empresas da ficha — o link para a ficha do Grupo, ou "Criar
          grupo". Logo depois do formulário: é sobre a ficha gravada, como as seções abaixo. */}
      <GrupoDaEmpresa key={`grupo-${empresaId}`} empresaId={empresaId} />
      {/* Desde a 13.11c em toda ficha carregada: "Cadastrada no Carga Máquina" ou "Ainda não está
          no Carga Máquina", e os selos de pendência e de reenvio da 5.5. */}
      <SincronizacaoComOErp key={`sincronizacao-${empresaId}`} empresaId={empresaId} />
      {/* 13.12 (FR-89): excluir a Empresa — o último bloco da ficha, só a quem é Responsável ou
          gestão e tem a permissão. Esconde, e não apaga. */}
      <ExclusaoDaEmpresa key={`exclusao-${empresaId}`} empresaId={empresaId} />
    </div>
  )
}

export const rotaEmpresaNova = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHO_DA_EMPRESA_NOVA,
  component: EmpresaNova,
})

export const rotaEmpresaEdicao = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHO_DA_EMPRESA,
  component: EmpresaEmEdicao,
})
